import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Wallet, Clock, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PageBackdrop } from "../components/PageBackdrop";

export default function MembershipPage() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [my, setMy] = useState({ balance: 0, purchases: [] });
    const [loading, setLoading] = useState(true);
    const [buyingId, setBuyingId] = useState(null);

    const loadAll = async () => {
        setLoading(true);
        try {
            const plansResp = await api.get("/membership-plans", { params: { active_only: true } });
            setPlans(plansResp.data || []);
            if (user) {
                const meResp = await api.get("/me/memberships");
                setMy(meResp.data || { balance: 0, purchases: [] });
            }
        } catch {
            /* noop */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

    const buy = async (plan) => {
        if (!user) { navigate("/login?next=/membership"); return; }
        setBuyingId(plan.id);
        try {
            await api.post("/membership-purchases", { plan_id: plan.id });
            toast.success("Request sent — you'll be notified once approved");
            await loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Could not send request"));
        } finally {
            setBuyingId(null);
        }
    };

    const pendingForPlan = (planId) => my.purchases.find((p) => p.plan_id === planId && p.status === "pending");

    return (
        <div className="relative isolate min-h-screen pt-24 pb-24 grain" data-testid="membership-page">
            <PageBackdrop testid="membership-bg" />
            <div className="max-w-6xl mx-auto px-6 lg:px-12">
                <div className="text-overline">Prepaid credit wallet</div>
                <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">Tresses <span className="italic gold-text">Membership</span>.</h1>
                <p className="text-[#7A5262] mt-4 max-w-xl">Top up once, save every visit. Prepay for salon credits and get bonus points on top — usable across every service you book with us.</p>

                {/* Balance widget for logged-in users */}
                {user && (
                    <div className="mt-10 card-luxe p-8 grain flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="membership-balance-widget">
                        <div className="flex items-center gap-4 flex-1">
                            <div className="w-14 h-14 bg-[#6B1535]/10 border border-[#C29A5B]/30 flex items-center justify-center">
                                <Wallet size={22} className="text-[#8F622C]" />
                            </div>
                            <div>
                                <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Your credit balance</div>
                                <div className="font-serif-display text-4xl gold-text mt-1" data-testid="membership-balance-amount">S${my.balance.toFixed(2)}</div>
                            </div>
                        </div>
                        <div className="text-[0.7rem] text-[#8A6574] max-w-xs">Apply your credits at checkout on any booking. Credits can be combined with loyalty points but not with promo coupons.</div>
                    </div>
                )}

                {/* Plans grid */}
                <h2 className="font-serif-display text-3xl text-[#3D0C1F] mt-16">Choose a plan</h2>
                {loading ? (
                    <div className="text-[#8A6574] mt-6 text-sm">Loading plans…</div>
                ) : plans.length === 0 ? (
                    <div className="card-luxe p-10 grain mt-6 text-[#7A5262] text-sm" data-testid="membership-empty">
                        No membership plans available right now. Please check back soon or contact us for a custom plan.
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="membership-plans-grid">
                        {plans.map((p) => {
                            const bonusPct = p.price > 0 ? Math.round(((p.credits - p.price) / p.price) * 100) : 0;
                            const pending = pendingForPlan(p.id);
                            return (
                                <div key={p.id} className="card-luxe p-8 grain flex flex-col" data-testid={`membership-plan-${p.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="font-serif-display text-2xl text-[#3D0C1F]">{p.name}</div>
                                        {bonusPct > 0 && (
                                            <span className="bg-[#6B1535] text-[#FFF8F2] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase font-semibold">+{bonusPct}% bonus</span>
                                        )}
                                    </div>
                                    <p className="text-[0.75rem] text-[#7A5262] mt-2 flex-1">{p.description}</p>
                                    <div className="mt-6 border-t border-[#6B1535]/10 pt-6">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-[0.65rem] tracking-[0.2em] uppercase text-[#8A6574]">Pay</span>
                                            <span className="font-serif-display text-2xl gold-text">S${p.price}</span>
                                        </div>
                                        <div className="flex items-baseline gap-2 mt-2">
                                            <span className="text-[0.65rem] tracking-[0.2em] uppercase text-[#8A6574]">Get</span>
                                            <span className="font-serif-display text-2xl text-[#3D0C1F]">S${p.credits} <span className="text-xs text-[#7A5262]">credits</span></span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-3 text-[0.65rem] text-[#8A6574]">
                                            <Clock size={11} /> Valid for {p.validity_days} days from approval
                                        </div>
                                    </div>
                                    {pending ? (
                                        <div className="mt-6 flex items-center gap-2 text-[0.7rem] tracking-[0.2em] uppercase text-[#8F622C] bg-[#6B1535]/10 border border-[#C29A5B]/30 px-3 py-2" data-testid={`membership-pending-${p.id}`}>
                                            <Sparkles size={12} /> Awaiting admin approval
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => buy(p)}
                                            disabled={buyingId === p.id}
                                            className="btn-gold mt-6 justify-center py-2 text-[0.7rem] tracking-[0.2em] disabled:opacity-60"
                                            data-testid={`buy-membership-${p.id}`}
                                        >
                                            {buyingId === p.id ? "Sending…" : (user ? "Buy Membership" : "Sign in to buy")}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* My memberships */}
                {user && my.purchases.length > 0 && (
                    <div className="mt-16" data-testid="my-memberships-list">
                        <h2 className="font-serif-display text-3xl text-[#3D0C1F]">Your memberships</h2>
                        <div className="mt-6 space-y-3">
                            {my.purchases.map((r) => (
                                <div key={r.id} className="card-luxe p-5 grain grid grid-cols-1 sm:grid-cols-5 gap-4 items-center" data-testid={`my-membership-${r.id}`}>
                                    <div className="sm:col-span-2">
                                        <div className="text-[#3D0C1F] font-medium">{r.plan_name}</div>
                                        <div className="text-[0.7rem] text-[#8A6574] mt-1">Paid S${r.price} · {r.credits} credits · {r.validity_days}d</div>
                                    </div>
                                    <div className="text-[0.7rem] text-[#7A5262]">
                                        Requested<br />{new Date(r.requested_at).toLocaleDateString()}
                                    </div>
                                    <div className="text-[0.7rem] text-[#7A5262]">
                                        {r.status === "approved" ? (
                                            <>Expires<br />{r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}</>
                                        ) : (
                                            <>Status<br /><span className="text-[#3D0C1F]">{r.status}</span></>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 justify-end">
                                        {r.status === "pending" && <Sparkles size={14} className="text-[#8F622C]" />}
                                        {r.status === "approved" && <CheckCircle2 size={14} className="text-emerald-700" />}
                                        {(r.status === "rejected" || r.status === "cancelled") && <XCircle size={14} className="text-red-700" />}
                                        {r.status === "approved" && (
                                            <span className="font-serif-display text-xl gold-text" data-testid={`my-membership-remaining-${r.id}`}>S${(r.credits_remaining ?? 0).toFixed(2)}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-[0.7rem] text-[#8A6574] mt-6">Once approved, apply your credits at checkout on any booking. <Link to="/book" className="text-[#8F622C] hover:underline">Book a service →</Link></p>
                    </div>
                )}
            </div>
        </div>
    );
}
